import type { AbstractControl } from '@angular/forms';
import { $localize } from '@angular/localize/init';
import type { FormlyFieldConfig } from '@ngx-formly/core';
import { EMPTY, merge, Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { generateId, getFormRoot } from './form-data.utility';
import type { FormlyHookFn } from './formly-hook-fn.type';

interface PVModule {
  id?: string;
  name?: string;
  panel_dc_rating_kw?: number | string | null;
  number_of_panels?: number | string | null;
}

const setValue = (control: AbstractControl | null, value: unknown): void => {
  if (control && control.value !== value) {
    control.setValue(value);
  }
};

const setOptions = (
  field: FormlyFieldConfig,
  options: { value: string; label: string }[]
): void => {
  if (JSON.stringify(field.props.options) !== JSON.stringify(options)) {
    field.props.options = options;
    field.options?.fieldChanges?.next({
      type: 'expressionChanges', field, property: 'props.options', value: options,
    });
    field.options?.detectChanges?.(field);
  }
};

// Queue until repeat controls have been registered, and release each row's subscription.
const watchForm = (
  field: FormlyFieldConfig,
  destroyed$: Observable<unknown>,
  refresh: () => void
): void => {
  const form = field.form?.root;
  if (!form || !field.formControl) {
    return;
  }

  let queued = false;
  const queueRefresh = () => {
    if (queued) {
      return;
    }
    queued = true;
    Promise.resolve().then(() => {
      queued = false;
      if (!subscription.closed) {
        refresh();
      }
    });
  };
  const subscription = merge(
    form.valueChanges,
    field.options?.fieldChanges?.pipe(filter((event) => event.type === 'valueChanges'))
      ?? EMPTY
  ).pipe(takeUntil(destroyed$)).subscribe(queueRefresh);
  const previousOnDestroy = field.hooks?.onDestroy;
  field.hooks = {
    ...field.hooks,
    onDestroy: (destroyedField) => {
      subscription.unsubscribe();
      if (typeof previousOnDestroy === 'function') {
        previousOnDestroy(destroyedField);
      }
    },
  };
  queueRefresh();
};

export const initializeDerPVModuleHook = (
  destroyed$: Observable<unknown>
): FormlyHookFn => (field) => {
  watchForm(field, destroyed$, () => {
    if (field.formControl.disabled) {
      return;
    }
    let row = field;
    while (row.parent && !row.parent.fieldArray) {
      row = row.parent;
    }
    const id = field.formControl.value || generateId(32);
    const name = `${$localize`PV Module`} ${Number(row.key) + 1}`;
    field.defaultValue = id;
    field.model.id = id;
    field.model.name = name;
    if (field.formControl.value !== id) {
      field.formControl.setValue(id, { emitEvent: false });
    }
    const nameControl = field.form.get('name');
    if (nameControl && nameControl.value !== name) {
      nameControl.setValue(name, { emitEvent: false });
    }
  });
};

export const mapDerPVModuleToArrayHook = (
  destroyed$: Observable<unknown>
): FormlyHookFn => (field) => {
  const root = getFormRoot(field);
  watchForm(field, destroyed$, () => {
    const equipment = field.form.root.get('equipment_information')?.getRawValue()
      ?? root.model?.equipment_information;
    if (![].concat(equipment?.energy_source || []).some(
      (source) => ['Solar', 'SolarStorage'].includes(source)
    )) {
      return;
    }
    const modules: PVModule[] = equipment?.pv_modules ?? [];
    setOptions(field, modules.map((module, index) => ({
      value: module.id,
      label: module.name || `${$localize`PV Module`} ${index + 1}`,
    })).filter((option) => !!option.value));
    if (field.formControl.disabled || field.props.readonly) {
      return;
    }

    const selectedId = field.formControl.value;
    const selected = modules.find((module) => module.id === selectedId);
    if (selectedId && !selected) {
      field.formControl.setValue(null);
    }
    const name = selected?.name || '';
    setValue(field.form.get('pv_module_name'), name);

    const rating = selected?.panel_dc_rating_kw;
    const count = selected?.number_of_panels;
    const hasInputs = rating !== null && rating !== undefined && rating !== ''
      && count !== null && count !== undefined && count !== '';
    const product = Number(rating) * Number(count);
    const generation = hasInputs && Number(rating) >= 0 && Number(count) >= 1
      && Number.isFinite(product) ? Number(product.toFixed(5)) : null;
    setValue(field.form.get('dc_size'), generation);
  });
};

export const mapRebatePVModuleOptionsHook = (
  destroyed$: Observable<unknown>
): FormlyHookFn => (field) => {
  const root = getFormRoot(field);
  watchForm(field, destroyed$, () => {
    const rebate = field.form.root.get('rebate_information')?.getRawValue()
      ?? root.model?.rebate_information;
    const options: { value: string; label: string }[] = [];
    for (let index = 0; index < 5; index++) {
      const module: PVModule = rebate?.[`pvmodule_${index}`];
      if (module?.id) {
        options.push({
          value: module.id,
          label: module.name || `${$localize`PV Module`} ${index + 1}`,
        });
      }
    }
    const selectedId = field.formControl.value;
    const selectedName = field.form.get('pv_module_name')?.value;
    if (selectedId && !options.some((option) => option.value === selectedId)) {
      options.push({ value: selectedId, label: selectedName || selectedId });
    }
    setOptions(field, options);
    if (!field.formControl.disabled && !field.props.readonly) {
      const selected = options.find((option) => option.value === selectedId);
      setValue(field.form.get('pv_module_name'), selected?.label || '');
    }
  });
};
